import React, { useState } from 'react';
import { questionDatabase } from '../../data/questionDatabase';
import './QuizSection.css';

const QuizSection = () => {
  const [isQuizActive, setIsQuizActive] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState({ correct: 0, incorrect: 0 });
  const [userAnswers, setUserAnswers] = useState([]);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [questionCount, setQuestionCount] = useState(10);
  const [autoPlayQuestion, setAutoPlayQuestion] = useState(false);
  const [questionTtsPlaying, setQuestionTtsPlaying] = useState(false);
  const [optionTtsPlaying, setOptionTtsPlaying] = useState({});
  const [feedbackTtsPlaying, setFeedbackTtsPlaying] = useState(false);
  const [sentenceTtsPlaying, setSentenceTtsPlaying] = useState(false);

  // Función para obtener preguntas aleatorias
  const getRandomQuestions = (count) => {
    const shuffled = [...questionDatabase.questions].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  // Función para generar texto de la pregunta actual para TTS
  const getCurrentQuestionText = () => {
    if (!isQuizActive || !selectedQuestions[currentQuestionIndex]) {
      return '';
    }
    
    const currentQuestion = selectedQuestions[currentQuestionIndex];
    // Reemplazar guiones bajos por "blank space" para mejor pronunciación
    const processedQuestion = currentQuestion.question
      .replace(/_+/g, 'blank space')
      .replace(/____/g, 'blank space')
      .replace(/___/g, 'blank space')
      .replace(/__/g, 'blank space')
      .replace(/_/g, 'blank space');
    
    return processedQuestion;
  };

  // Función para generar texto de una opción específica
  const getOptionText = (option) => {
    // También procesar las opciones por si tienen guiones bajos
    return option
      .replace(/_+/g, 'blank space')
      .replace(/____/g, 'blank space')
      .replace(/___/g, 'blank space')
      .replace(/__/g, 'blank space')
      .replace(/_/g, 'blank space');
  };

  // Función para obtener explicación teórica según la categoría
  const getExplanation = (category, question, correctOption, userOption, options) => {
    const explanations = {
      "First Conditional": {
        explanation: "El First Conditional (Primera Condicional) se utiliza para expresar situaciones reales o muy probables en el futuro. Su estructura es: If + presente simple, will + verbo base. Esta estructura indica que si se cumple la condición, es muy probable que ocurra el resultado. Se diferencia del Second Conditional porque las situaciones son realistas y factibles.",
        detailed: "💡 Detalles importantes: La cláusula con 'if' siempre va en presente simple, nunca en futuro. El resultado puede usar 'will', 'can', 'may', 'must', etc. Se puede invertir el orden: 'I will call you if I need help' o 'If I need help, I will call you'. También se usa para advertencias, promesas, amenazas y predicciones basadas en hechos actuales.",
        examples: "Ejemplos con traducción:\n• 'If it rains tomorrow, we will cancel the picnic.' → 'Si llueve mañana, cancelaremos el picnic.'\n• 'If you study hard, you will pass the exam.' → 'Si estudias duro, aprobarás el examen.'\n• 'I will help you if you ask me.' → 'Te ayudaré si me lo pides.'",
        commonMistakes: "❌ Errores frecuentes:\n• NO decir: 'If it will rain...' ✅ Correcto: 'If it rains...'\n• NO mezclar tiempos: 'If you will study, you pass' ✅ Correcto: 'If you study, you will pass'\n• Recordar la coma cuando 'if' va al principio de la oración"
      },
      "Second Conditional": {
        explanation: "El Second Conditional (Segunda Condicional) expresa situaciones hipotéticas, improbables o imaginarias en el presente/futuro. Su estructura es: If + pasado simple, would + verbo base. Se usa para hablar de 'qué pasaría si...' en situaciones que son poco probables o completamente imaginarias. Es el condicional de los sueños y las fantasías.",
        detailed: "💡 Detalles importantes: Siempre usa 'were' (no 'was') para todas las personas con el verbo 'be': 'If I were you...', 'If she were here...'. Se puede usar para dar consejos de forma cortés: 'If I were you, I would study more'. También para expresar deseos imposibles o muy improbables en el presente.",
        examples: "Ejemplos con traducción:\n• 'If I were rich, I would travel the world.' → 'Si fuera rico, viajaría por el mundo.'\n• 'If she had time, she would help us.' → 'Si ella tuviera tiempo, nos ayudaría.'\n• 'If I could fly, I would visit you every day.' → 'Si pudiera volar, te visitaría todos los días.'",
        commonMistakes: "❌ Errores frecuentes:\n• NO decir: 'If I was you...' ✅ Correcto: 'If I were you...'\n• NO usar 'will': 'If I had money, I will buy...' ✅ Correcto: 'If I had money, I would buy...'\n• Confundir con First Conditional para situaciones probables"
      },
      "Third Conditional": {
        explanation: "El Third Conditional (Tercera Condicional) se refiere a situaciones pasadas que NO ocurrieron. Su estructura es: If + past perfect, would have + past participle. Se usa para expresar arrepentimiento, especular sobre el pasado, o imaginar cómo habrían sido las cosas si hubieran sido diferentes. Es el condicional del 'qué habría pasado si...'",
        detailed: "💡 Detalles importantes: Siempre habla del pasado y de cosas que NO pasaron. Se usa para: arrepentimientos ('If I had studied, I would have passed'), críticas indirectas ('If you had told me, I would have helped'), y especulaciones sobre el pasado ('If dinosaurs hadn't died, humans wouldn't have evolved'). También se puede usar 'could have' o 'might have' en lugar de 'would have'.",
        examples: "Ejemplos con traducción:\n• 'If I had studied, I would have passed the exam.' → 'Si hubiera estudiado, habría aprobado el examen.'\n• 'If they had left earlier, they would have arrived on time.' → 'Si hubieran salido antes, habrían llegado a tiempo.'\n• 'If she had called me, I would have helped her.' → 'Si me hubiera llamado, la habría ayudado.'",
        commonMistakes: "❌ Errores frecuentes:\n• NO decir: 'If I would have known...' ✅ Correcto: 'If I had known...'\n• NO olvidar 'had': 'If I studied...' ✅ Correcto: 'If I had studied...'\n• Confundir los tiempos: usar presente en lugar de past perfect"
      },
      "Present Perfect": {
        explanation: "El Present Perfect conecta el pasado con el presente. Su estructura es: have/has + past participle. Se usa para: acciones que comenzaron en el pasado y continúan (o pueden continuar) en el presente, experiencias de vida, acciones pasadas con relevancia presente, y acciones recién completadas. Es como un puente entre el pasado y el presente.",
        detailed: "💡 Detalles importantes: Se usa con 'for' (duración) y 'since' (punto de inicio), 'just' (recién), 'already' (ya), 'yet' (todavía/ya), 'ever/never' (alguna vez/nunca). NO se usa con expresiones de tiempo específicas del pasado (yesterday, last week, in 1990). Se enfoca en el resultado presente de una acción pasada.",
        examples: "Ejemplos con traducción:\n• 'I have lived here for 5 years.' → 'He vivido aquí durante 5 años.' (y sigo viviendo)\n• 'She has just finished her homework.' → 'Ella acaba de terminar su tarea.'\n• 'Have you ever been to Paris?' → '¿Has estado alguna vez en París?'",
        commonMistakes: "❌ Errores frecuentes:\n• NO decir: 'I have went' ✅ Correcto: 'I have gone'\n• NO usar con tiempo específico: 'I have seen him yesterday' ✅ Correcto: 'I saw him yesterday'\n• Confundir 'been' y 'gone': 'been' = fui y regresé, 'gone' = fui y no he regresado"
      },
      "Past Perfect": {
        explanation: "El Past Perfect indica que una acción ocurrió ANTES que otra acción en el pasado. Su estructura es: had + past participle. Se usa para establecer secuencias temporales claras cuando hablamos de dos o más eventos pasados. Es como decir 'esto pasó primero, luego esto otro'. Es esencial para narrar historias y establecer orden cronológico.",
        detailed: "💡 Detalles importantes: Siempre necesitas un contexto de 'pasado en el pasado'. Se usa con 'before', 'after', 'when', 'by the time', 'already', 'just'. También en Third Conditional y en reported speech. Si el orden temporal es claro por el contexto, a veces se puede omitir y usar Past Simple.",
        examples: "Ejemplos con traducción:\n• 'I had eaten before you arrived.' → 'Yo había comido antes de que llegaras.'\n• 'She had already left when I called.' → 'Ella ya se había ido cuando llamé.'\n• 'By the time we arrived, the movie had started.' → 'Para cuando llegamos, la película ya había empezado.'",
        commonMistakes: "❌ Errores frecuentes:\n• Usar innecesariamente: 'I had lunch and had gone home' ✅ Correcto: 'I had lunch and went home'\n• NO distinguir del Present Perfect: Past Perfect necesita contexto pasado\n• Olvidar 'had': 'I eaten before you arrived' ✅ Correcto: 'I had eaten before you arrived'"
      },
      "Passive Voice": {
        explanation: "La Voz Pasiva se usa cuando queremos enfocar la acción o el resultado más que quien realiza la acción. Su estructura es: be + past participle. El objeto de la oración activa se convierte en el sujeto de la pasiva. Se usa en contextos formales, científicos, cuando no sabemos quién hizo algo, o cuando no es importante mencionar al agente.",
        detailed: "💡 Detalles importantes: Se puede usar en todos los tiempos verbales (presente, pasado, futuro, etc.). El agente se introduce con 'by' si es relevante: 'The book was written by Shakespeare'. Se omite el agente cuando es obvio, desconocido o irrelevante. Muy común en noticias, instrucciones, y textos académicos.",
        examples: "Ejemplos con traducción:\n• 'The book was written by Shakespeare.' → 'El libro fue escrito por Shakespeare.'\n• 'The house is being painted.' → 'La casa está siendo pintada.'\n• 'The project will be completed next week.' → 'El proyecto será completado la próxima semana.'",
        commonMistakes: "❌ Errores frecuentes:\n• Usar cuando la activa es más natural: 'Mistakes were made by me' ✅ Mejor: 'I made mistakes'\n• Errores en la conjugación: 'The car was being wash' ✅ Correcto: 'The car was being washed'\n• Olvidar el past participle: 'The door was open' vs 'The door was opened' (diferentes significados)"
      },
      "Modal Verbs": {
        explanation: "Los Modal Verbs (can, could, may, might, must, should, will, would, ought to) son verbos auxiliares que expresan habilidad, posibilidad, obligación, permiso, sugerencia, o probabilidad. Van seguidos de verbo en infinitivo SIN 'to'. Cada modal tiene varios significados según el contexto y cambian el matiz de la oración principal.",
        detailed: "💡 Detalles importantes: No cambian de forma (no agregan 's' en tercera persona), no necesitan auxiliares para negativo/pregunta. 'Can' = habilidad/permiso, 'Could' = pasado de can/cortesía/posibilidad, 'May/Might' = posibilidad/permiso formal, 'Must' = obligación fuerte/deducción, 'Should' = consejo/obligación moral, 'Would' = cortesía/situaciones hipotéticas.",
        examples: "Ejemplos con traducción:\n• 'You should study more.' → 'Deberías estudiar más.' (consejo)\n• 'I can speak English.' → 'Puedo hablar inglés.' (habilidad)\n• 'You must be careful.' → 'Debes ser cuidadoso.' (obligación)\n• 'It might rain tomorrow.' → 'Podría llover mañana.' (posibilidad)",
        commonMistakes: "❌ Errores frecuentes:\n• Agregar 's': 'He cans speak' ✅ Correcto: 'He can speak'\n• Usar 'to': 'I must to go' ✅ Correcto: 'I must go'\n• Confundir significados: 'may' (permiso formal) vs 'can' (permiso informal)\n• Doble modal: 'I will can do it' ✅ Correcto: 'I will be able to do it'"
      },
      "Gerunds and Infinitives": {
        explanation: "Los Gerundios (-ing) y los Infinitivos (to + verb) se comportan como sustantivos en la oración. Algunos verbos requieren gerundio, otros infinitivo, y algunos ambos pero con significados diferentes. No hay regla universal; se debe memorizar qué forma sigue cada verbo. Es una de las áreas más complicadas del inglés.",
        detailed: "💡 Detalles importantes: Verbos + gerundio: enjoy, finish, avoid, mind, suggest, etc. Verbos + infinitivo: want, decide, plan, hope, promise, etc. Verbos que cambian significado: 'stop smoking' (dejar de fumar) vs 'stop to smoke' (parar para fumar), 'remember doing' (recordar haber hecho) vs 'remember to do' (recordar que hay que hacer).",
        examples: "Ejemplos con traducción:\n• 'I enjoy reading books.' → 'Disfruto leyendo libros.' (gerundio)\n• 'I want to learn Spanish.' → 'Quiero aprender español.' (infinitivo)\n• 'I stopped smoking.' → 'Dejé de fumar.' vs 'I stopped to smoke.' → 'Paré para fumar.'",
        commonMistakes: "❌ Errores frecuentes:\n• 'I enjoy to read' ✅ Correcto: 'I enjoy reading'\n• 'I want going' ✅ Correcto: 'I want to go'\n• Confundir significados: 'I forgot to call' (no llamé) vs 'I forgot calling' (no recuerdo haber llamado)\n• No reconocer cuando -ing es gerundio vs participio"
      },
      "Relative Clauses": {
        explanation: "Las Relative Clauses (cláusulas relativas) proporcionan información adicional sobre un sustantivo en la oración. Se introducen con pronombres relativos: who (personas), which (cosas), that (personas/cosas), where (lugares), when (tiempo), whose (posesión). Pueden ser defining (esenciales, sin comas) o non-defining (información extra, con comas).",
        detailed: "💡 Detalles importantes: Defining clauses (restrictivas): información esencial, sin comas, se puede omitir el pronombre si es objeto. Non-defining clauses (no restrictivas): información extra, con comas, nunca se omite el pronombre. 'That' solo en defining clauses. 'Which' puede referirse a toda la oración anterior. 'Whose' indica posesión (de quien/cuyo).",
        examples: "Ejemplos con traducción:\n• 'The man who called you is here.' → 'El hombre que te llamó está aquí.' (defining)\n• 'My brother, who lives in London, is a doctor.' → 'Mi hermano, que vive en Londres, es doctor.' (non-defining)\n• 'The book that I bought is interesting.' → 'El libro que compré es interesante.'",
        commonMistakes: "❌ Errores frecuentes:\n• Usar 'what': 'The book what I read' ✅ Correcto: 'The book that I read'\n• Comas incorrectas: 'My brother who lives in London...' (implica varios hermanos) vs 'My brother, who lives in London...' (un solo hermano)\n• Doble sujeto: 'The man who he called' ✅ Correcto: 'The man who called'"
      },
      "Phrasal Verbs": {
        explanation: "Los Phrasal Verbs son combinaciones de un verbo + preposición/adverbio que crean un significado completamente nuevo, diferente al verbo original. Son fundamentales en inglés cotidiano y profesional. Pueden ser separables (objeto puede ir en el medio) o inseparables (objeto siempre va al final). Su significado es idiomático, no literal.",
        detailed: "💡 Detalles importantes: SEPARABLES: 'turn on/off' → 'turn the TV on' o 'turn on the TV'. Con pronombres SIEMPRE separar: 'turn it on' (nunca 'turn on it'). INSEPARABLES: 'look for', 'run into' → objeto siempre al final: 'look for something'. TRES PALABRAS: 'get along with', 'run out of' → nunca separar. Cada phrasal verb puede tener múltiples significados según contexto.",
        examples: "📚 EJEMPLOS ESENCIALES POR CATEGORÍAS:\n\n🏠 VIDA DIARIA:\n• 'wake up' → despertarse | 'I wake up at 7 AM' → 'Me despierto a las 7'\n• 'get up' → levantarse | 'Get up! You're late!' → '¡Levántate! Llegas tarde!'\n• 'turn on/off' → encender/apagar | 'Turn off the lights' → 'Apaga las luces'\n• 'clean up' → limpiar | 'Clean up your room' → 'Limpia tu cuarto'\n• 'pick up' → recoger | 'Pick up your toys' → 'Recoge tus juguetes'\n\n💼 TRABAJO:\n• 'set up' → establecer/configurar | 'Set up a meeting' → 'Organiza una reunión'\n• 'carry out' → llevar a cabo | 'Carry out the plan' → 'Ejecuta el plan'\n• 'put off' → posponer | 'Put off the meeting' → 'Pospón la reunión'\n• 'work out' → resolver/funcionar | 'Work out the problem' → 'Resuelve el problema'\n• 'bring up' → mencionar/sacar tema | 'Bring up the issue' → 'Menciona el problema'\n\n🚗 MOVIMIENTO:\n• 'get in/out' → subirse/bajarse (carro) | 'Get in the car' → 'Súbete al carro'\n• 'get on/off' → subirse/bajarse (transporte público) | 'Get on the bus' → 'Súbete al autobús'\n• 'run into' → encontrarse por casualidad | 'I ran into my teacher' → 'Me encontré con mi maestro'\n• 'drop off' → dejar (persona/cosa) | 'Drop me off here' → 'Déjame aquí'\n• 'pick up' → recoger (persona) | 'Pick me up at 5' → 'Recógeme a las 5'\n\n💭 RELACIONES:\n• 'get along with' → llevarse bien | 'I get along with my boss' → 'Me llevo bien con mi jefe'\n• 'break up' → terminar relación | 'They broke up last week' → 'Terminaron la semana pasada'\n• 'make up' → reconciliarse | 'They made up after the fight' → 'Se reconciliaron después de la pelea'\n• 'look up to' → admirar | 'I look up to my father' → 'Admiro a mi padre'\n• 'put up with' → tolerar/aguantar | 'I can't put up with this noise' → 'No puedo tolerar este ruido'",
        commonMistakes: "⚠️ ERRORES MUY COMUNES:\n\n❌ SEPARACIÓN INCORRECTA:\n• 'turn on it' ✅ 'turn it on' (con pronombres SIEMPRE separar)\n• 'look for it' ✅ CORRECTO (inseparable, objeto al final)\n• 'I ran into to him' ✅ 'I ran into him' (no duplicar preposición)\n\n❌ USAR EL VERBO BASE EN LUGAR DEL PHRASAL:\n• 'I encountered my friend' ✅ MEJOR: 'I ran into my friend' (más natural)\n• 'I tolerance his behavior' ✅ 'I put up with his behavior'\n• 'I admiration my teacher' ✅ 'I look up to my teacher'\n\n❌ CONFUSIÓN DE SIGNIFICADOS:\n• 'look for' ≠ 'look at' | buscar ≠ mirar\n• 'get up' ≠ 'wake up' | levantarse ≠ despertarse\n• 'turn on' ≠ 'turn up' | encender ≠ subir volumen/aparecer\n• 'put on' ≠ 'put off' | ponerse ropa ≠ posponer\n\n❌ NO RECONOCER PHRASAL VERBS:\n• Muchos estudiantes no reconocen que 'give up' (rendirse) es diferente a 'give' (dar)\n• 'take off' puede ser: quitarse ropa, despegar avión, o irse rápidamente"
      },
      "Dates and Numbers": {
        explanation: "Los Dates and Numbers (fechas y números) en inglés tienen reglas específicas de pronunciación, escritura y uso. Los números ordinales (1st, 2nd, 3rd) se usan para fechas, posiciones y orden. Los cardinales (1, 2, 3) para cantidad. Las fechas tienen formatos diferentes en inglés americano vs británico. Es fundamental dominar estos conceptos para comunicación formal e informal.",
        detailed: "💡 Detalles importantes: NÚMEROS ORDINALES: 1st=first, 2nd=second, 3rd=third, 4th=fourth, 5th=fifth, etc. REGLA ESPECIAL: teens (11th-19th) terminan en 'th'. FECHAS: American format = Month Day, Year (January 1st, 2024). British format = Day Month Year (1st January 2024). PRONUNCIACIÓN: 'twentieth' = /ˈtwentɪəθ/, 'thirteenth' = /ˌθɜːrˈtiːnθ/. ESCRITURA: Se puede escribir '20th' o 'twentieth'.",
        examples: "🔢 NÚMEROS ORDINALES COMPLETOS:\n\n📊 BÁSICOS (1-20):\n• 1st = first /fɜːrst/ → 'primero'\n• 2nd = second /ˈsekənd/ → 'segundo'\n• 3rd = third /θɜːrd/ → 'tercero'\n• 4th = fourth /fɔːrθ/ → 'cuarto'\n• 5th = fifth /fɪfθ/ → 'quinto'\n• 6th = sixth /sɪksθ/ → 'sexto'\n• 7th = seventh /ˈsevənθ/ → 'séptimo'\n• 8th = eighth /eɪtθ/ → 'octavo'\n• 9th = ninth /naɪnθ/ → 'noveno'\n• 10th = tenth /tenθ/ → 'décimo'\n• 11th = eleventh /ɪˈlevənθ/ → 'undécimo'\n• 12th = twelfth /twelfθ/ → 'duodécimo'\n• 13th = thirteenth /ˌθɜːrˈtiːnθ/ → 'decimotercero'\n• 20th = twentieth /ˈtwentɪəθ/ → 'vigésimo'\n\n📅 FECHAS REALES:\n• 'January 1st, 2024' → 'El primero de enero de 2024'\n• 'March 15th' → 'El quince de marzo'\n• 'December 31st' → 'El treinta y uno de diciembre'\n• 'My birthday is on the 25th' → 'Mi cumpleaños es el 25'\n\n🏆 POSICIONES Y RANKINGS:\n• 'She finished first in the race' → 'Ella terminó primera en la carrera'\n• 'This is my second attempt' → 'Este es mi segundo intento'\n• 'He lives on the tenth floor' → 'Él vive en el décimo piso'\n• 'It's the twenty-first century' → 'Es el siglo veintiuno'",
        commonMistakes: "❌ ERRORES MUY FRECUENTES:\n\n🔢 CONFUSIÓN CARDINAL vs ORDINAL:\n• 'Today is twenty January' ✅ 'Today is the twentieth of January' o 'January twentieth'\n• 'I live in floor five' ✅ 'I live on the fifth floor'\n• 'This is chapter two' ✅ 'This is the second chapter' (cuando hablamos de orden)\n\n📝 ESCRITURA INCORRECTA:\n• 'twentyth' ✅ 'twentieth' (no agregar 'th' a 'twenty')\n• 'thirdth' ✅ 'third' (no duplicar 'th')\n• 'nineth' ✅ 'ninth' (se omite la 'e' de 'nine')\n• 'fiveth' ✅ 'fifth' (cambio de 've' a 'f')\n\n🗓️ FORMATO DE FECHAS:\n• 'The 25 of December' ✅ 'The 25th of December' o 'December 25th'\n• 'January the five' ✅ 'January the fifth' o 'January 5th'\n• Mixing formats: 'The January 5th' ✅ 'January 5th' o 'The 5th of January'\n\n🗣️ PRONUNCIACIÓN:\n• Decir 'twenty' cuando es 'twentieth' → /ˈtwentɪəθ/ (con sonido 'th' al final)\n• Confundir 'thirteen' /θɜːrˈtiːn/ con 'thirty' /ˈθɜːrti/\n• No pronunciar la 'th' final en ordinales: 'fif' en lugar de 'fifth' /fɪfθ/"
      },
      "Comparatives": {
        explanation: "Los Comparatives (comparativos) se usan para comparar dos personas, objetos o situaciones. Hay dos formas principales: agregar '-er' para adjetivos cortos (tall → taller) o usar 'more' para adjetivos largos (beautiful → more beautiful). Siempre van seguidos de 'than'. Son esenciales para expresar diferencias, preferencias y hacer comparaciones en la vida diaria.",
        detailed: "💡 Detalles importantes: REGLA DE SÍLABAS: 1 sílaba → add '-er' (big → bigger). 2+ sílabas → use 'more' (expensive → more expensive). EXCEPCIONES: 2 sílabas terminados en '-y' → cambia 'y' por 'ier' (happy → happier, easy → easier). IRREGULARES: good → better, bad → worse, far → further/farther. DOBLE CONSONANTE: Si termina en consonante-vocal-consonante, dobla la última (hot → hotter, thin → thinner).",
        examples: "📊 COMPARATIVOS POR CATEGORÍAS:\n\n📏 ADJETIVOS CORTOS (1 sílaba + '-er'):\n• 'tall → taller' | 'John is taller than Mary' → 'John es más alto que Mary'\n• 'fast → faster' | 'This car is faster than that one' → 'Este carro es más rápido que ese'\n• 'cheap → cheaper' | 'Apples are cheaper than mangoes' → 'Las manzanas son más baratas que los mangos'\n• 'old → older' | 'My brother is older than me' → 'Mi hermano es mayor que yo'\n• 'big → bigger' | 'This house is bigger than ours' → 'Esta casa es más grande que la nuestra'\n\n📱 ADJETIVOS LARGOS (2+ sílabas + 'more'):\n• 'expensive → more expensive' | 'iPhones are more expensive than Androids' → 'Los iPhones son más caros que los Androids'\n• 'beautiful → more beautiful' | 'Roses are more beautiful than weeds' → 'Las rosas son más hermosas que las malas hierbas'\n• 'comfortable → more comfortable' | 'This chair is more comfortable than that one' → 'Esta silla es más cómoda que esa'\n• 'portable → more portable' | 'Laptops are more portable than desktops' → 'Las laptops son más portátiles que las computadoras de escritorio'\n• 'intelligent → more intelligent' | 'Dolphins are more intelligent than fish' → 'Los delfines son más inteligentes que los peces'\n\n💫 CASOS ESPECIALES (2 sílabas con '-y' → '-ier'):\n• 'happy → happier' | 'She looks happier today' → 'Ella se ve más feliz hoy'\n• 'easy → easier' | 'Spanish is easier than Chinese' → 'El español es más fácil que el chino'\n• 'funny → funnier' | 'This joke is funnier than the last one' → 'Este chiste es más gracioso que el anterior'\n• 'busy → busier' | 'Monday is busier than Sunday' → 'El lunes está más ocupado que el domingo'\n\n🌟 COMPARATIVOS IRREGULARES:\n• 'good → better' | 'This pizza is better than yesterday's' → 'Esta pizza está mejor que la de ayer'\n• 'bad → worse' | 'Today's weather is worse than yesterday's' → 'El clima de hoy está peor que el de ayer'\n• 'far → farther/further' | 'My house is farther than yours' → 'Mi casa está más lejos que la tuya'",
        commonMistakes: "❌ ERRORES MUY COMUNES:\n\n🚫 DOBLE COMPARATIVO (nunca uses ambos):\n• 'more better' ✅ 'better' (nunca combines 'more' con '-er')\n• 'more cheaper' ✅ 'cheaper' (usa solo una forma)\n• 'more faster' ✅ 'faster' (adjetivos cortos solo usan '-er')\n\n📏 CONFUSIÓN DE REGLAS DE SÍLABAS:\n• 'beautifuler' ✅ 'more beautiful' (adjetivos largos usan 'more')\n• 'more big' ✅ 'bigger' (adjetivos cortos usan '-er')\n• 'expensiver' ✅ 'more expensive' (4+ sílabas siempre 'more')\n\n✏️ ERRORES DE ORTOGRAFÍA:\n• 'biger' ✅ 'bigger' (doblar consonante: big → bigger)\n• 'hoter' ✅ 'hotter' (doblar consonante: hot → hotter)\n• 'happyer' ✅ 'happier' (cambiar 'y' por 'ier': happy → happier)\n• 'easyer' ✅ 'easier' (cambiar 'y' por 'ier': easy → easier)\n\n🔗 OLVIDAR 'THAN':\n• 'This car is faster' ✅ 'This car is faster than that one' (comparativo necesita 'than')\n• 'She is more intelligent' ✅ 'She is more intelligent than her sister'\n\n🔄 USAR SUPERLATIVO EN LUGAR DE COMPARATIVO:\n• 'This is the better of the two' ✅ 'This is the better one' o 'This is better than that one'\n• 'Between A and B, A is the most expensive' ✅ 'A is more expensive than B'\n\n🌍 COMPARACIONES ILÓGICAS:\n• 'My English is better than Maria' ✅ 'My English is better than Maria's English' o 'than Maria's'\n• 'This car is faster than John' ✅ 'This car is faster than John's car'"
      },
      "Superlatives": {
        explanation: "Los Superlatives (superlativos) se usan para indicar el grado máximo de una cualidad entre tres o más elementos. Expresan 'el más...' o 'el menos...'. Hay dos formas: agregar '-est' para adjetivos cortos (tall → the tallest) o usar 'the most' para adjetivos largos (beautiful → the most beautiful). Siempre van precedidos del artículo 'the'.",
        detailed: "💡 Detalles importantes: REGLA DE SÍLABAS: 1 sílaba → add 'the + -est' (big → the biggest). 2+ sílabas → use 'the most' (expensive → the most expensive). EXCEPCIONES: 2 sílabas terminados en '-y' → cambia 'y' por 'iest' (happy → the happiest). IRREGULARES: good → the best, bad → the worst, far → the farthest/furthest. SIEMPRE usar 'the' antes del superlativo. Se puede usar 'in' (lugar) o 'of' (grupo): 'the tallest in the class', 'the best of all'.",
        examples: "🏆 SUPERLATIVOS POR CATEGORÍAS:\n\n📏 ADJETIVOS CORTOS (1 sílaba + 'the -est'):\n• 'tall → the tallest' | 'John is the tallest student in the class' → 'John es el estudiante más alto de la clase'\n• 'fast → the fastest' | 'This is the fastest car in the world' → 'Este es el carro más rápido del mundo'\n• 'cheap → the cheapest' | 'Apples are the cheapest fruit here' → 'Las manzanas son la fruta más barata aquí'\n• 'old → the oldest' | 'She is the oldest person in our family' → 'Ella es la persona más mayor de nuestra familia'\n• 'big → the biggest' | 'This is the biggest house on the street' → 'Esta es la casa más grande de la calle'\n\n📱 ADJETIVOS LARGOS (2+ sílabas + 'the most'):\n• 'expensive → the most expensive' | 'This is the most expensive restaurant in town' → 'Este es el restaurante más caro de la ciudad'\n• 'beautiful → the most beautiful' | 'She is the most beautiful woman I know' → 'Ella es la mujer más hermosa que conozco'\n• 'comfortable → the most comfortable' | 'This is the most comfortable chair ever' → 'Esta es la silla más cómoda jamás'\n• 'intelligent → the most intelligent' | 'Dolphins are the most intelligent sea animals' → 'Los delfines son los animales marinos más inteligentes'\n• 'popular → the most popular' | 'Instagram is the most popular social media' → 'Instagram es la red social más popular'\n\n💫 CASOS ESPECIALES (2 sílabas con '-y' → 'the -iest'):\n• 'happy → the happiest' | 'Today is the happiest day of my life' → 'Hoy es el día más feliz de mi vida'\n• 'easy → the easiest' | 'Spanish is the easiest language to learn' → 'El español es el idioma más fácil de aprender'\n• 'funny → the funniest' | 'He tells the funniest jokes' → 'Él cuenta los chistes más graciosos'\n• 'busy → the busiest' | 'Monday is the busiest day of the week' → 'El lunes es el día más ocupado de la semana'\n\n🌟 SUPERLATIVOS IRREGULARES:\n• 'good → the best' | 'This is the best pizza in the city' → 'Esta es la mejor pizza de la ciudad'\n• 'bad → the worst' | 'That was the worst movie ever' → 'Esa fue la peor película jamás'\n• 'far → the farthest/furthest' | 'Pluto is the farthest planet from the sun' → 'Plutón es el planeta más lejano del sol'\n\n🎯 SUPERLATIVOS CON PREPOSICIONES:\n• 'the tallest IN the class' → 'el más alto DE la clase' (lugar/grupo específico)\n• 'the best OF all students' → 'el mejor DE todos los estudiantes' (selección de grupo)\n• 'the most expensive IN the store' → 'el más caro DE la tienda' (ubicación)",
        commonMistakes: "❌ ERRORES MUY COMUNES:\n\n🚫 OLVIDAR EL ARTÍCULO 'THE':\n• 'He is tallest student' ✅ 'He is THE tallest student' (superlativo SIEMPRE lleva 'the')\n• 'This is most expensive car' ✅ 'This is THE most expensive car'\n• 'She is best singer' ✅ 'She is THE best singer'\n\n📏 CONFUSIÓN DE REGLAS DE SÍLABAS:\n• 'the beautifulest' ✅ 'the most beautiful' (adjetivos largos usan 'the most')\n• 'the most big' ✅ 'the biggest' (adjetivos cortos usan 'the -est')\n• 'the expensivest' ✅ 'the most expensive' (3+ sílabas siempre 'the most')\n\n✏️ ERRORES DE ORTOGRAFÍA:\n• 'the bigest' ✅ 'the biggest' (doblar consonante: big → biggest)\n• 'the hotest' ✅ 'the hottest' (doblar consonante: hot → hottest)\n• 'the happyest' ✅ 'the happiest' (cambiar 'y' por 'iest': happy → happiest)\n• 'the easyest' ✅ 'the easiest' (cambiar 'y' por 'iest': easy → easiest)\n\n🔄 CONFUNDIR COMPARATIVO CON SUPERLATIVO:\n• 'Between A, B, and C, A is better' ✅ 'A is THE BEST' (3+ elementos = superlativo)\n• 'This is more expensive of all' ✅ 'This is THE MOST expensive of all'\n• 'She is taller in the group' ✅ 'She is THE tallest in the group'\n\n📍 PREPOSICIONES INCORRECTAS:\n• 'the best at the class' ✅ 'the best IN the class' (usar 'in' para lugares/grupos)\n• 'the tallest from all' ✅ 'the tallest OF all' (usar 'of' para selección de grupo)\n• 'the fastest on the world' ✅ 'the fastest IN the world' (usar 'in' para lugares)\n\n🌍 COMPARACIONES ILÓGICAS:\n• 'John is the tallest than everyone' ✅ 'John is the tallest OF everyone' o 'John is taller than everyone'\n• 'This is the most better option' ✅ 'This is the best option' (no mezclar formas)"
      }
    };

    const categoryInfo = explanations[category] || {
      explanation: "Esta es una pregunta de gramática inglesa. Analiza las opciones cuidadosamente y considera las reglas gramaticales aplicables.",
      detailed: "Revisa las reglas específicas de esta categoría gramatical y practica con más ejemplos.",
      examples: "Busca ejemplos similares para reforzar tu comprensión.",
      commonMistakes: "Presta atención a los detalles gramaticales y al contexto de la oración."
    };

    // Limpiar y procesar la pregunta para la traducción
    let cleanQuestion = question;
    const correctAnswer = options[correctOption];
    
    // Detectar si hay duplicación (misma frase aparece dos veces)
    const words = cleanQuestion.split(' ');
    const duplicatePattern = new RegExp(`\\b${correctAnswer.replace(/\s+/g, '\\s+')}\\s+${correctAnswer.replace(/\s+/g, '\\s+')}\\b`, 'gi');
    
    if (duplicatePattern.test(cleanQuestion)) {
      // Remover duplicación y luego reemplazar guiones bajos
      cleanQuestion = cleanQuestion.replace(duplicatePattern, correctAnswer);
    } else {
      // Caso normal: solo reemplazar guiones bajos
      cleanQuestion = cleanQuestion.replace(/_{2,}/g, correctAnswer);
    }
    
    return {
      category: category,
      questionText: cleanQuestion,
      explanation: categoryInfo.explanation,
      detailedInfo: categoryInfo.detailed,
      examples: categoryInfo.examples,
      commonMistakes: categoryInfo.commonMistakes,
      correctAnswer: `La respuesta correcta es: "${options[correctOption]}"`,
      userAnswer: userOption !== null ? `Tu respuesta fue: "${options[userOption]}"` : "",
      isCorrect: userOption === correctOption
    };
  };

  // Función para abrir Google Translate con el texto preseleccionado
  const openGoogleTranslate = (text) => {
    const encodedText = encodeURIComponent(text);
    const translateUrl = `https://translate.google.com/?sl=en&tl=es&text=${encodedText}`;
    window.open(translateUrl, '_blank');
  };

  // Función para manejar TTS de la pregunta
  const handleQuestionTTS = () => {
    if (questionTtsPlaying) {
      // Pausar/detener
      window.speechSynthesis.cancel();
      setQuestionTtsPlaying(false);
    } else {
      // Reproducir
      const utterance = new SpeechSynthesisUtterance(getCurrentQuestionText());
      utterance.lang = 'en-US';
      utterance.rate = 0.7; // Más lento
      utterance.pitch = 1.0;
      
      // Configurar voz en inglés
      const voices = window.speechSynthesis.getVoices();
      const englishVoice = voices.find(voice => 
        voice.lang.includes('en') || voice.lang.includes('EN')
      );
      if (englishVoice) {
        utterance.voice = englishVoice;
      }
      
      utterance.onstart = () => setQuestionTtsPlaying(true);
      utterance.onend = () => setQuestionTtsPlaying(false);
      utterance.onerror = () => setQuestionTtsPlaying(false);
      
      window.speechSynthesis.speak(utterance);
    }
  };

  // Función para manejar TTS de opciones
  const handleOptionTTS = (optionIndex, optionText) => {
    const isPlaying = optionTtsPlaying[optionIndex];
    
    if (isPlaying) {
      // Pausar/detener
      window.speechSynthesis.cancel();
      setOptionTtsPlaying(prev => ({ ...prev, [optionIndex]: false }));
    } else {
      // Detener cualquier otra opción que esté reproduciéndose
      window.speechSynthesis.cancel();
      setOptionTtsPlaying({});
      
      // Procesar el texto de la opción
      const processedText = getOptionText(optionText);
      
      // Reproducir nueva opción
      const utterance = new SpeechSynthesisUtterance(processedText);
      utterance.lang = 'en-US';
      utterance.rate = 0.7; // Más lento
      utterance.pitch = 1.0;
      
      // Configurar voz en inglés
      const voices = window.speechSynthesis.getVoices();
      const englishVoice = voices.find(voice => 
        voice.lang.includes('en') || voice.lang.includes('EN')
      );
      if (englishVoice) {
        utterance.voice = englishVoice;
      }
      
      utterance.onstart = () => setOptionTtsPlaying(prev => ({ ...prev, [optionIndex]: true }));
      utterance.onend = () => setOptionTtsPlaying(prev => ({ ...prev, [optionIndex]: false }));
      utterance.onerror = () => setOptionTtsPlaying(prev => ({ ...prev, [optionIndex]: false }));
      
      window.speechSynthesis.speak(utterance);
    }
  };

  // Función para TTS del feedback
  const handleFeedbackTTS = (isCorrect, correctOption, userOption, options) => {
    if (feedbackTtsPlaying) {
      window.speechSynthesis.cancel();
      setFeedbackTtsPlaying(false);
      return;
    }

    let feedbackText = "";
    if (isCorrect) {
      feedbackText = `Correct! The answer is: ${getOptionText(options[correctOption])}`;
    } else {
      feedbackText = `Incorrect. The correct answer is: ${getOptionText(options[correctOption])}`;
    }

    const utterance = new SpeechSynthesisUtterance(feedbackText);
    utterance.lang = 'en-US';
    utterance.rate = 0.7;
    utterance.pitch = 1.0;
    
    // Configurar voz en inglés
    const voices = window.speechSynthesis.getVoices();
    const englishVoice = voices.find(voice => 
      voice.lang.includes('en') || voice.lang.includes('EN')
    );
    if (englishVoice) {
      utterance.voice = englishVoice;
    }
    
    utterance.onstart = () => setFeedbackTtsPlaying(true);
    utterance.onend = () => setFeedbackTtsPlaying(false);
    utterance.onerror = () => setFeedbackTtsPlaying(false);
    
    window.speechSynthesis.speak(utterance);
  };

  // Función para TTS de la oración completa en inglés
  const handleSentenceTTS = (text) => {
    if (sentenceTtsPlaying) {
      window.speechSynthesis.cancel();
      setSentenceTtsPlaying(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 0.7;
    utterance.pitch = 1.0;
    
    // Configurar voz en inglés
    const voices = window.speechSynthesis.getVoices();
    const englishVoice = voices.find(voice => 
      voice.lang.includes('en') || voice.lang.includes('EN')
    );
    if (englishVoice) {
      utterance.voice = englishVoice;
    }
    
    utterance.onstart = () => setSentenceTtsPlaying(true);
    utterance.onend = () => setSentenceTtsPlaying(false);
    utterance.onerror = () => setSentenceTtsPlaying(false);
    
    window.speechSynthesis.speak(utterance);
  };

  // Función para iniciar el quiz
  const startQuiz = () => {
    const randomQuestions = getRandomQuestions(questionCount);
    setSelectedQuestions(randomQuestions);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setScore({ correct: 0, incorrect: 0 });
    setUserAnswers([]);
    setQuizCompleted(false);
    setIsQuizActive(true);
  };

  // Función para seleccionar respuesta
  const selectAnswer = (answerIndex) => {
    if (selectedAnswer !== null) return;
    
    setSelectedAnswer(answerIndex);
    setShowFeedback(true);
    
    const currentQuestion = selectedQuestions[currentQuestionIndex];
    const isCorrect = answerIndex === currentQuestion.correct;
    
    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      incorrect: prev.incorrect + (isCorrect ? 0 : 1)
    }));
    
    setUserAnswers(prev => [...prev, {
      question: currentQuestion.question,
      userAnswer: currentQuestion.options[answerIndex],
      correctAnswer: currentQuestion.options[currentQuestion.correct],
      isCorrect,
      category: currentQuestion.category
    }]);
  };

  // Función para ir a la siguiente pregunta
  const nextQuestion = () => {
    // Detener cualquier TTS activo
    window.speechSynthesis.cancel();
    setQuestionTtsPlaying(false);
    setOptionTtsPlaying({});
    setFeedbackTtsPlaying(false);
    setSentenceTtsPlaying(false);
    
    if (currentQuestionIndex + 1 >= selectedQuestions.length) {
      setQuizCompleted(true);
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
    }
  };

  // Función para reiniciar el quiz
  const resetQuiz = () => {
    // Detener cualquier TTS activo
    window.speechSynthesis.cancel();
    setQuestionTtsPlaying(false);
    setOptionTtsPlaying({});
    setFeedbackTtsPlaying(false);
    setSentenceTtsPlaying(false);
    
    setIsQuizActive(false);
    setSelectedQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setScore({ correct: 0, incorrect: 0 });
    setUserAnswers([]);
    setQuizCompleted(false);
  };

  // Obtener el emoji y mensaje final
  const getFinalMessage = () => {
    const percentage = (score.correct / selectedQuestions.length) * 100;
    if (percentage === 100) return { emoji: '🏆', message: '¡PERFECTO! ¡Eres un maestro de la gramática!' };
    if (percentage >= 80) return { emoji: '🌟', message: '¡Excelente! Dominas muy bien los temas.' };
    if (percentage >= 60) return { emoji: '👍', message: '¡Bien hecho! Sigue practicando para mejorar.' };
    if (percentage >= 40) return { emoji: '📚', message: 'Necesitas repasar algunos temas.' };
    return { emoji: '💪', message: '¡No te rindas! Revisa los temas y vuelve a intentarlo.' };
  };

  if (!isQuizActive) {
    return (
      <div className="category-section quiz-section">
        <h2 className="category-title">🎮 ZONA DE PRÁCTICA - TEST YOUR KNOWLEDGE!</h2>
        
        <div className="quiz-setup">
          <div className="question-count-selector">
            <label htmlFor="questionCount">Número de preguntas:</label>
            <select 
              id="questionCount"
              value={questionCount} 
              onChange={(e) => setQuestionCount(Number(e.target.value))}
              className="question-count-select"
            >
              <option value={5}>5 preguntas</option>
              <option value={10}>10 preguntas</option>
              <option value={15}>15 preguntas</option>
              <option value={20}>20 preguntas</option>
              <option value={30}>30 preguntas</option>
              <option value={50}>50 preguntas</option>
            </select>
          </div>
          
          <div className="quiz-buttons">
            <button 
              className="start-quiz-btn"
              onClick={startQuiz}
            >
              � COMENZAR PRÁCTICA
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (quizCompleted) {
    const finalMessage = getFinalMessage();
    
    return (
      <div className="category-section quiz-section">
        <h2 className="category-title">🎮 ZONA DE PRÁCTICA - TEST YOUR KNOWLEDGE!</h2>
        
        <div className="quiz-stats">
          <span className="stat correct">✅ Correctas: {score.correct}</span>
          <span className="stat incorrect">❌ Incorrectas: {score.incorrect}</span>
          <span className="stat progress">📊 Total: {score.correct}/{selectedQuestions.length}</span>
        </div>

        <div className="results-card">
          <h2>🏆 ¡Práctica Completada!</h2>
          <div className="final-score">{finalMessage.emoji} {score.correct}/{selectedQuestions.length}</div>
          <div className="final-message">{finalMessage.message}</div>
          
          <div className="review-container">
            <h3>📝 Revisión de respuestas:</h3>
            {userAnswers.map((answer, index) => (
              <div key={index} className={`review-item ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
                <div className="review-category">{answer.isCorrect ? '✅' : '❌'} {answer.category}</div>
                <div className="review-question">{answer.question}</div>
                <div className="review-answer">Tu respuesta: <strong>{answer.userAnswer}</strong></div>
                {!answer.isCorrect && (
                  <div className="review-correct">Respuesta correcta: <strong>{answer.correctAnswer}</strong></div>
                )}
              </div>
            ))}
          </div>
          
          <button className="reset-quiz-btn" onClick={resetQuiz}>
            🔄 HACER OTRO QUIZ
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = selectedQuestions[currentQuestionIndex];
  
  return (
    <div className="category-section quiz-section">
      <h2 className="category-title">🎮 ZONA DE PRÁCTICA - TEST YOUR KNOWLEDGE!</h2>
      
      <div className="quiz-stats">
        <span className="stat correct">✅ Correctas: {score.correct}</span>
        <span className="stat incorrect">❌ Incorrectas: {score.incorrect}</span>
        <span className="stat progress">📊 Progreso: {currentQuestionIndex + 1}/{selectedQuestions.length}</span>
      </div>

      <div className="question-card">
        <div className="question-header">
          <span className="question-number">Pregunta {currentQuestionIndex + 1}/{selectedQuestions.length}</span>
          <span className="question-category">{currentQuestion.category}</span>
        </div>
        
        <div className="question-text-container">
          <div className="question-text">{currentQuestion.question}</div>
          <button 
            className={`tts-inline-button ${questionTtsPlaying ? 'playing' : ''}`}
            onClick={handleQuestionTTS}
            title={questionTtsPlaying ? "Pausar pregunta" : "Escuchar pregunta"}
          >
            {questionTtsPlaying ? '⏹' : '▶'}
          </button>
        </div>
        
        <div className="options-container">
          {currentQuestion.options.map((option, index) => (
            <div key={index} className="option-wrapper">
              <button
                className={`option-button ${
                  selectedAnswer !== null 
                    ? selectedAnswer === index 
                      ? index === currentQuestion.correct ? 'correct' : 'incorrect'
                      : index === currentQuestion.correct ? 'correct' : ''
                    : ''
                }`}
                onClick={() => selectAnswer(index)}
                disabled={selectedAnswer !== null}
              >
                {option}
              </button>
              <button 
                className={`tts-option-button ${optionTtsPlaying[index] ? 'playing' : ''}`}
                onClick={() => handleOptionTTS(index, option)}
                title={optionTtsPlaying[index] ? "Pausar opción" : "Escuchar opción"}
              >
                {optionTtsPlaying[index] ? '⏹' : '▶'}
              </button>
            </div>
          ))}
        </div>
        
        {showFeedback && (
          <div className="">
            {/* Botón superior centrado */}
            <div className="next-button-container">
              <button className="next-button" onClick={nextQuestion}>
                {currentQuestionIndex + 1 >= selectedQuestions.length ? 'Ver Resultados' : 'Siguiente Pregunta →'}
              </button>
            </div>
            
            <div className={`feedback-result ${selectedAnswer === currentQuestion.correct ? 'correct' : 'incorrect'}`}>
              <div className="feedback-text">
                {selectedAnswer === currentQuestion.correct 
                  ? '✅ ¡Correcto! Excelente trabajo.' 
                  : `❌ Incorrecto. La respuesta correcta es: ${currentQuestion.options[currentQuestion.correct]}`
                }
              </div>
              <button 
                className={`tts-feedback-button ${feedbackTtsPlaying ? 'playing' : ''}`}
                onClick={() => handleFeedbackTTS(
                  selectedAnswer === currentQuestion.correct,
                  currentQuestion.correct,
                  selectedAnswer,
                  currentQuestion.options
                )}
                title={feedbackTtsPlaying ? "Pausar respuesta" : "Escuchar respuesta correcta"}
              >
                {feedbackTtsPlaying ? '⏹' : '▶'}
              </button>
            </div>
            
            {(() => {
              const explanation = getExplanation(
                currentQuestion.category,
                currentQuestion.question,
                currentQuestion.correct,
                selectedAnswer,
                currentQuestion.options
              );
              
              return (
                <div className="theory-section">
                  <h4>📚 Teoría Completa - {explanation.category}</h4>
                  
                  <div className="translation-section">
                    <h5>🔤 Traducción de la oración:</h5>
                    <div className="sentence-container">
                      <p className="translation-text">
                        <strong>Inglés:</strong> {explanation.questionText}
                      </p>
                      <button 
                        className={`tts-inline-button ${sentenceTtsPlaying ? 'playing' : ''}`}
                        onClick={() => handleSentenceTTS(explanation.questionText)}
                        title={sentenceTtsPlaying ? "Pausar oración" : "Escuchar oración completa"}
                      >
                        {sentenceTtsPlaying ? '⏹' : '▶'}
                      </button>
                    </div>
                    <p className="translation-text">
                      <strong>Español:</strong> 
                      <span 
                        className="translate-link"
                        onClick={() => openGoogleTranslate(explanation.questionText)}
                        title="Traducir con Google Translate"
                      >
                        Ver traducción aquí
                      </span>
                    </p>
                  </div>
                  
                  <div className="theory-content">
                    <div className="theory-block">
                      <h5>📖 Explicación General:</h5>
                      <p>{explanation.explanation}</p>
                    </div>
                    
                    <div className="theory-block">
                      <h5>💡 Información Detallada:</h5>
                      <p>{explanation.detailedInfo}</p>
                    </div>
                    
                    <div className="theory-block">
                      <h5>🌟 Ejemplos con Traducción:</h5>
                      <div className="examples-text">
                        {explanation.examples.split('\n').map((line, index) => (
                          <p key={index} className="example-line">{line}</p>
                        ))}
                      </div>
                    </div>
                    
                    <div className="theory-block">
                      <h5>⚠️ Errores Comunes:</h5>
                      <div className="mistakes-text">
                        {explanation.commonMistakes.split('\n').map((line, index) => (
                          <p key={index} className="mistake-line">{line}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            {/* Botón inferior centrado */}
            <div className="next-button-container">
              <button className="next-button" onClick={nextQuestion}>
                {currentQuestionIndex + 1 >= selectedQuestions.length ? 'Ver Resultados' : 'Siguiente Pregunta →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizSection;
